import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import Swal from 'sweetalert2';

import { SCUIBreadCrumb } from '@/atomic/molecules/SCUIBreadCrumb/SCUIBreadCrumb';
import { SCUIContent } from '@/atomic/organisms/SCUIContent/SCUIContent';
import { SCUILoadingLinear } from '@/atomic/atoms/SCUILoadingLinear/SCUILoadingLinear';
import { PageUrl } from '@/types/enums/pageUrl-enums';
import { MasterDataQuery } from '@/services/query';
import CategoryRestrictionQuery from '@/services/query/categoryRestriction/CategoryRestrictionQuery.services';
import CategoryRestrictionCommand from '@/services/command/categoryRestriction/CategoryRestrictionCommand.services';
import { FilterBar, CategoryHierarchy, ActiveRestrictions } from './components';
import {
    CategoryNode,
    Country,
    ZoneCategoryIdByTypeName,
    ZoneTypeName,
} from './growCategoryRestriction.types';
import {
    extractCategoryNodes,
    getApiErrorMessage,
    getSelectedCountryId,
    normalizeCategoryTree,
} from './growCategoryRestriction.helpers';
import { useRestrictionState } from './hooks';
import { GCR } from './growCategoryRestriction.classes';

export const GrowCategoryRestriction = () => {
    const { t } = useTranslation();

    const [countries, setCountries] = useState<Country[]>([]);
    const [isLoadingCountries, setIsLoadingCountries] = useState(false);

    const [selectedCountryId, setSelectedCountryId] = useState<number | ''>(getSelectedCountryId());
    const [typeName, setTypeName] = useState<ZoneTypeName | ''>('');
    const [searchText, setSearchText] = useState('');
    const [isFiltering, setIsFiltering] = useState(false);
    const [filterNodes, setFilterNodes] = useState<CategoryNode[] | null>(null);

    const [hierarchyReloadKey, setHierarchyReloadKey] = useState(0);
    const [isRestricting, setIsRestricting] = useState(false);

    const {
        restrictedIds,
        restrictionTree,
        hasRestrictionChanges,
        resetRestrictionState,
        discoverRestrictedIds,
        toggleRestrict,
        getPendingLeafNodes,
    } = useRestrictionState();

    useEffect(() => {
        setIsLoadingCountries(true);
        MasterDataQuery.GetCountryList()
            .then((res: any) => {
                if (res?.data) {
                    setCountries(res.data);
                    const storedId = getSelectedCountryId();
                    if (storedId !== '' && res.data.some((c: Country) => c.countryId === storedId)) {
                        setSelectedCountryId(storedId);
                    } else if (res.data.length > 0) {
                        setSelectedCountryId(res.data[0].countryId);
                    }
                }
            })
            .finally(() => setIsLoadingCountries(false));
    }, []);

    const handleCountryChange = useCallback(
        (id: number) => {
            setSelectedCountryId(id);
            setFilterNodes(null);
            resetRestrictionState();
        },
        [resetRestrictionState]
    );

    const handleClearInputs = useCallback(() => {
        setTypeName('');
        setSearchText('');
        setSelectedCountryId(getSelectedCountryId());
    }, []);

    /** Clears filter inputs AND reloads the zone-level tree. Keeps Active Restrictions edits. */
    const handleClearFilter = useCallback(() => {
        setSelectedCountryId(getSelectedCountryId());
        setTypeName('');
        setSearchText('');
        setFilterNodes(null);
    }, []);

    const handleFilter = useCallback(() => {
        if (selectedCountryId === '') {
            Swal.fire({
                icon: 'warning',
                title: t('general.warning'),
                text: t('growCategoryRestriction.filter.selectCountry'),
                confirmButtonText: t('general.ok'),
            });
            return;
        }

        setIsFiltering(true);
        const params: Record<string, string | number> = { CountryId: selectedCountryId };
        if (searchText.trim()) params.SearchText = searchText.trim();
        if (typeName) params.ZoneCategoryId = ZoneCategoryIdByTypeName[typeName];

        CategoryRestrictionQuery.SearchCategoryRestriction(params)
            .then((res: any) => {
                const items = extractCategoryNodes(res);
                if (items.length === 0) {
                    // Fall back to the default children tree when search returns nothing.
                    setFilterNodes(null);
                    setHierarchyReloadKey((k) => k + 1);
                    Swal.fire({
                        icon: 'info',
                        title: t('general.noDataFound'),
                        text: '',
                        showConfirmButton: false,
                        timer: 3000,
                    });
                    return;
                }
                setFilterNodes(normalizeCategoryTree(items));
            })
            .catch(() => {
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: t('growCategoryRestriction.searchError'),
                    confirmButtonText: t('general.ok'),
                });
            })
            .finally(() => setIsFiltering(false));
    }, [selectedCountryId, searchText, typeName, t]);

    const handleRestrict = useCallback(() => {
        if (selectedCountryId === '') {
            Swal.fire({
                icon: 'warning',
                title: t('general.warning'),
                text: t('growCategoryRestriction.filter.selectCountry'),
                confirmButtonText: t('general.ok'),
            });
            return;
        }

        const leafNodes = getPendingLeafNodes();
        if (leafNodes.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t('general.warning'),
                text: t('growCategoryRestriction.restrictions.noSelections'),
                confirmButtonText: t('general.ok'),
            });
            return;
        }

        const restrictions = leafNodes.map((n) => ({
            categoryId: n.categoryId,
            isRestricted: restrictedIds.has(n.categoryId),
        }));

        setIsRestricting(true);
        CategoryRestrictionCommand.SaveCategoryRestrictions({
            countryId: selectedCountryId,
            restrictions,
        })
            .then(() => {
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('growCategoryRestriction.restrictions.saveSuccess'),
                    showConfirmButton: false,
                    timer: 2500,
                });
                resetRestrictionState();
                setFilterNodes(null);
                setTypeName('');
                setSearchText('');
                setHierarchyReloadKey((k) => k + 1);
            })
            .catch((err: any) => {
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: getApiErrorMessage(err, t('general.unknownError')),
                    confirmButtonText: t('general.ok'),
                });
            })
            .finally(() => setIsRestricting(false));
    }, [selectedCountryId, restrictedIds, getPendingLeafNodes, resetRestrictionState, t]);

    return (
        <SCUIContent className={GCR.page}>
            <SCUILoadingLinear display={isLoadingCountries || isFiltering || isRestricting} />

            <Box sx={{ px: '24px', pt: '24px', pb: 0 }}>
                <SCUIBreadCrumb
                    title={t('growCategoryRestriction.title')}
                    componentLink={PageUrl.growCategoryRestriction.url}
                />
            </Box>

            <FilterBar
                countries={countries}
                selectedCountryId={selectedCountryId}
                onCountryChange={handleCountryChange}
                typeName={typeName}
                onTypeNameChange={setTypeName}
                searchText={searchText}
                onSearchTextChange={setSearchText}
                onClear={handleClearInputs}
                onFilter={handleFilter}
                onRestrict={handleRestrict}
                isFiltering={isFiltering}
                isRestricting={isRestricting}
                hasRestrictionChanges={hasRestrictionChanges}
            />

            <Box
                className={GCR.panelsWrapper}
                sx={{
                    display: 'flex',
                    gap: '16px',
                    px: '20px',
                    py: '16px',
                    flex: 1,
                    minHeight: 0,
                    height: 'calc(100vh - 215px)',
                    background: 'linear-gradient(180deg, #f0f4ff 0%, #f5f7fa 100%)',
                }}
            >
                <CategoryHierarchy
                    countryId={selectedCountryId}
                    overrideNodes={filterNodes}
                    restrictedIds={restrictedIds}
                    onToggleRestrict={toggleRestrict}
                    onDiscoverRestrictedIds={discoverRestrictedIds}
                    onClearFilter={handleClearFilter}
                    reloadKey={hierarchyReloadKey}
                    isRestricting={isRestricting}
                />
                <ActiveRestrictions
                    restrictionTree={restrictionTree}
                    restrictedIds={restrictedIds}
                    onToggleRestrict={toggleRestrict}
                    isRestricting={isRestricting}
                />
            </Box>
        </SCUIContent>
    );
};
